import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Title,
  Text,
  Paper,
  Loader,
  Stack,
  Group,
  Badge,
  RingProgress,
  Center,
  Divider,
  Accordion,
} from '@mantine/core';
import { PageContainer } from '../../../components/ui/PageContainer';
import { LoadFailed } from '../../../components/ui/LoadFailed';
import { useGetPerformanceReviewRoundsIdResults } from '../../../api/generated/performance-review/performance-review';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../../store/userStore';
import { SubjectGoalsPanel } from '../parts/SubjectGoalsPanel';
import { scoreExpectation, weightedMean } from '../parts/score';
import { BlockDelta, RoundComparisonView } from '../parts/BlockDelta';
import { overallFirst } from '../parts/helpers';

interface BlockResult {
  block: string;
  scoreMean?: number;
  // Total (respondent × scale-question) answers contributing to scoreMean —
  // not distinct respondents.
  responseCount: number;
}

interface SummaryBlock {
  block: string;
  comment: string;
}

interface ResultEntry {
  _id?: string;
  user?: { _id?: string; name?: string; avatar?: string } | string;
  summaryPublished?: string;
  // Absent while the summary is only pre-published (lead/PM can read it, the
  // subject cannot).
  summaryPublishedAt?: string;
  summaryBlocks?: SummaryBlock[];
  blocks?: BlockResult[];
  comparison?: RoundComparisonView | null;
}

const entryUserId = (p: ResultEntry): string | undefined =>
  typeof p.user === 'object' ? p.user?._id : undefined;

export const PerformanceReviewRoundResults: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const me = useUserStore((s) => s.user);
  const expDefaults = t('perfReview.expectationScale', { returnObjects: true });
  const expLabels = Array.isArray(expDefaults) ? (expDefaults as string[]) : undefined;
  const [openItems, setOpenItems] = useState<string[]>([]);
  const { data: results, isLoading } = useGetPerformanceReviewRoundsIdResults(id!);
  const [searchParams] = useSearchParams();
  const focusUserId = searchParams.get('user');

  // Пришли по ссылке на конкретного человека — раскрываем его сразу, иначе
  // страница встречает свёрнутым списком, в котором его ещё надо найти.
  // Ровно один раз: запрос перезапрашивается при возврате фокуса на вкладку, и
  // без этого флага строка раскрывалась бы заново поверх того, что свернул сам.
  const isFocusApplied = useRef(false);
  useEffect(() => {
    if (!focusUserId || isFocusApplied.current) return;
    const entries = (results as unknown as ResultEntry[]) || [];
    const index = entries.findIndex((p) => entryUserId(p) === focusUserId);
    if (index < 0) return;
    isFocusApplied.current = true;
    setOpenItems([entries[index]._id ?? focusUserId]);
  }, [focusUserId, results]);

  if (isLoading)
    return (
      <PageContainer>
        <Loader />
      </PageContainer>
    );
  const list = (results as unknown as ResultEntry[]) || [];
  if (!list.length) {
    return (
      <PageContainer>
        <LoadFailed
          message={t('perfReview.noResults', 'Результаты пока недоступны')}
          onBack={() => navigate('/performance-review')}
        />
      </PageContainer>
    );
  }

  const entryName = (p: ResultEntry): string =>
    typeof p.user === 'object'
      ? (p.user?.name ?? t('perfReview.unknownParticipant'))
      : (p.user ?? t('perfReview.unknownParticipant'));

  // The subject viewing their OWN single result doesn't need their name repeated
  // ("he knows who he is"). Admins/leads see many people, so we keep names and
  // group per-person in a collapsible accordion for easy scanning.
  const isSelfOnly = list.length === 1 && !!me?._id && entryUserId(list[0]) === me._id;

  // Per-person body: the block cards (+ legacy fallback). No name — the caller
  // renders the name (accordion control) or omits it (self view).
  const renderBody = (p: ResultEntry) => {
    const scored = p.blocks ?? [];
    const summaryByBlock = new Map<string, string>();
    for (const sb of p.summaryBlocks || []) summaryByBlock.set(sb.block, sb.comment);
    // Блоки саммари, которых нет среди оценённых: их не было в peer-вопросах
    // (как «Оценка за период») либо анкету правили после написания саммари.
    // Раньше они шли отдельным хвостом в конце — из-за этого общий вывод
    // оказывался ниже всех частных блоков.
    const unscored = (p.summaryBlocks || [])
      .filter((sb) => !scored.find((b) => b.block === sb.block))
      .map((sb) => ({ block: sb.block, scoreMean: undefined }));
    const blocks = overallFirst([...scored, ...unscored]);
    const hasBlockUI = blocks.length > 0;
    const deltaByBlock = new Map((p.comparison?.blocks ?? []).map((r) => [r.block, r]));
    // Итог по всем блокам сразу — иначе читатель складывает шесть колец в уме.
    const total = weightedMean(scored);
    const totalMeta = total ? scoreExpectation(total.mean, expLabels) : null;

    return (
      <>
        {p.comparison?.professionChanged && (
          <Text size="sm" c="dimmed" mb="md">
            {t('perfReview.dynamics.professionChanged')}
          </Text>
        )}
        {p.comparison && !p.comparison.professionChanged && (
          <Text size="xs" c="dimmed" mb="md">
            {t('perfReview.dynamics.comparedTo', { round: p.comparison.previousRoundTitle })}
          </Text>
        )}
        {total && totalMeta && (
          <Paper withBorder p="md" radius="md" mb="md">
            <Group align="center" wrap="nowrap" gap="md">
              <RingProgress
                size={84}
                thickness={8}
                sections={[{ value: (total.mean / 5) * 100, color: totalMeta.color }]}
                label={
                  <Center>
                    <Text fw={700} size="md">
                      {total.mean.toFixed(1)}
                    </Text>
                  </Center>
                }
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} mb={2}>
                  {t('perfReview.overallScore')}
                </Text>
                {totalMeta.label && (
                  <Text size="xs" fw={600} c={totalMeta.color} mb={4}>
                    {totalMeta.label}
                  </Text>
                )}
                <Text size="sm" c="dimmed">
                  {t('perfReview.overallScoreHint', { count: total.count })}
                </Text>
                {/* Дельты здесь намеренно нет: кольцо показывает взвешенное
                    среднее по всем блокам, а сравнение даёт движение по каждому
                    блоку отдельно. Подставить сюда дельту блока общей оценки
                    значило бы приписать одному числу движение другого, а
                    честного «прошлого итога» в ответе нет — сопоставились не
                    все блоки. Движение видно на карточках блоков ниже. */}
              </div>
            </Group>
          </Paper>
        )}

        {hasBlockUI && (
          <Stack gap="md">
            {blocks.map((b) => {
              const comment = summaryByBlock.get(b.block);
              const hasScore = typeof b.scoreMean === 'number';
              const meta = hasScore ? scoreExpectation(b.scoreMean as number, expLabels) : null;
              return (
                <Paper key={b.block} withBorder p="md" radius="md">
                  <Group align="flex-start" wrap="nowrap" gap="md">
                    {meta ? (
                      <RingProgress
                        size={84}
                        thickness={8}
                        sections={[
                          {
                            value: ((b.scoreMean as number) / 5) * 100,
                            color: meta.color,
                          },
                        ]}
                        label={
                          <Center>
                            <Text fw={700} size="md">
                              {(b.scoreMean as number).toFixed(1)}
                            </Text>
                          </Center>
                        }
                      />
                    ) : (
                      <Badge variant="light" color="gray">
                        {t('perfReview.noScore')}
                      </Badge>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} mb={meta?.label ? 2 : 4}>
                        {b.block}
                      </Text>
                      <Group gap={8} mb={4} wrap="wrap">
                        {meta?.label && (
                          <Text size="xs" fw={600} c={meta.color}>
                            {meta.label}
                          </Text>
                        )}
                        <BlockDelta row={deltaByBlock.get(b.block)} />
                      </Group>
                      {comment ? (
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {comment}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed" fs="italic">
                          {t('perfReview.noBlockSummary')}
                        </Text>
                      )}
                    </div>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        )}

        {/* Legacy fallback: a free-text published summary from old rounds. */}
        {!hasBlockUI && p.summaryPublished && (
          <>
            <Divider my="md" />
            <Text size="sm" c="dimmed" mt="md" fw={500}>
              {t('perfReview.finalSummary')}
            </Text>
            <Text mt={4} style={{ whiteSpace: 'pre-wrap' }}>
              {p.summaryPublished}
            </Text>
          </>
        )}
      </>
    );
  };

  return (
    <PageContainer>
      <Title order={2} mb="md">
        {t('perfReview.results', 'Результаты')}
      </Title>

      {isSelfOnly ? (
        <Paper withBorder p="md">
          {/* Own result: the agreements are read-only here — accepting and closing
              them still happens on the profile's Goals tab, which is where the
              buttons the backend actually allows live. */}
          {entryUserId(list[0]) && <SubjectGoalsPanel userId={entryUserId(list[0])!} />}
          {renderBody(list[0])}
        </Paper>
      ) : (
        <Accordion multiple variant="separated" value={openItems} onChange={setOpenItems}>
          {list.map((p, i) => {
            // _id is optional on the type; fall back to a stable per-index value
            // so two entries without an _id don't collapse onto the same
            // accordion value (which would open/close them together).
            const itemValue = p._id ?? entryUserId(p) ?? `entry-${i}`;
            const isOpen = openItems.includes(itemValue);
            return (
              <Accordion.Item key={itemValue} value={itemValue}>
                <Accordion.Control>
                  <Group gap="xs">
                    <Text fw={600}>{entryName(p)}</Text>
                    {/* Pre-published: readable here, but the person themselves has
                        not seen it yet. Without this the lead walks into the 1-on-1
                        assuming they have. */}
                    {!p.summaryPublishedAt && (
                      <Badge size="sm" variant="light" color="blue">
                        {t('perfReview.notPublishedToSubject')}
                      </Badge>
                    )}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {/* This page is the lead's/PM's prep for the 1-on-1, so the
                      agreements get written down right where the summary is read.
                      Mounted only once the row is expanded — an accordion panel's
                      children render even while collapsed, and an admin opening a
                      round with 19 people would otherwise fire 19 goal requests. */}
                  {isOpen && entryUserId(p) && (
                    <SubjectGoalsPanel userId={entryUserId(p)!} canAssign />
                  )}
                  {renderBody(p)}
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </PageContainer>
  );
};
